import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { getEventAttendees, checkInAttendee } from '../../../services/event.service';
import { supabase } from '../../../lib/supabase';
import { Event, EventAttendee } from '../../../types/event';
import { useToast } from '../../../hooks/useToast';
import { formatDate } from '../../../utils/dateFormatter';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Eye, Copy } from 'lucide-react';

const EventAttendees: React.FC = () => {
  const params = useParams();
  const id = params.id;
  console.log('Params bruts:', params);
  console.log('ID extrait:', id);
  const { showToast } = useToast();
  
  const [event, setEvent] = useState<Event | null>(null);
  const [attendees, setAttendees] = useState<EventAttendee[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [checkingInId, setCheckingInId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filter, setFilter] = useState<'all' | 'checked_in' | 'pending'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [showIdModal, setShowIdModal] = useState(false);
  const itemsPerPageOptions = [50, 100, 200, 500];
  
  useEffect(() => {
    const fetchEventAndAttendees = async () => {
      if (!id) {
        console.log('ID manquant dans les paramètres URL');
        return;
      }
      
      console.log('Récupération de l\'événement avec ID:', id);
      
      try {
        setLoading(true);
        
        // Récupérer l'événement directement avec l'ID extrait des paramètres d'URL
        console.log('Utilisation de l\'ID extrait des paramètres:', id);
        
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .eq('id', id)
          .single();
        
        if (error) {
          console.error('Erreur Supabase lors de la récupération de l\'événement:', error);
          throw error;
        }
        
        if (!data) {
          console.error('Aucun événement trouvé avec l\'ID');
          throw new Error(`Événement non trouvé`);
        }
        
        console.log('Événement récupéré avec ID:', data);
        setEvent(data);
        
        let attendeesData;
        try {
          // Utiliser l'ID extrait des paramètres pour les participants
          attendeesData = await getEventAttendees(id);
          console.log('Participants récupérés:', attendeesData.length);
        } catch (attendeesError: any) {
          console.error('Erreur lors de la récupération des participants:', attendeesError);
          throw attendeesError;
        }
        
        setEvent(data);
        setAttendees(attendeesData);
      } catch (error: any) {
        console.error('Erreur globale:', error);
        showToast(error.message || 'Erreur lors du chargement des données', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEventAndAttendees();
  }, [id, showToast]);
  
  const handleCheckIn = async (attendeeId: string) => {
    try {
      setCheckingInId(attendeeId);
      const updatedAttendee = await checkInAttendee(attendeeId);
      console.log('Résultat checkInAttendee:', updatedAttendee);
      setAttendees(prev => 
        prev.map(a => a.id === attendeeId ? updatedAttendee : a)
      );
      showToast('Participant enregistré avec succès', 'success');
    } catch (error: any) {
      console.error('Erreur checkInAttendee:', error);
      showToast(error.message || 'Erreur lors de l\'enregistrement du participant', 'error');
    } finally {
      setCheckingInId(null);
    }
  };
  
  const filteredAttendees = attendees.filter(attendee => {
    const matchesSearch = searchTerm === '' || 
      (attendee.user?.name && attendee.user.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (attendee.user?.email && attendee.user.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (attendee.attendee_name && attendee.attendee_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (attendee.attendee_email && attendee.attendee_email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (attendee.qr_code && attendee.qr_code.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = filter === 'all' || 
      (filter === 'checked_in' && attendee.status === 'checked_in') ||
      (filter === 'pending' && attendee.status === 'pending');
    
    return matchesSearch && matchesFilter;
  });

  const totalFiltered = filteredAttendees.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const paginatedAttendees = filteredAttendees.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Si la page courante dépasse le total, revenir à la dernière page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Réinitialiser à la première page quand les filtres changent
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchTerm, itemsPerPage]);
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  if (!event) {
    return (
      <AdminLayout>
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-900/20 border-l-4 border-red-500 p-4 text-red-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18l-7-7m0 0l7-7m-7 7h18"></path>
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-200">
                  Événement non trouvé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  if (showIdModal) {
    return (
      <AdminLayout>
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-gray-900 p-6 rounded-lg max-w-md w-full border border-white/20">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">ID de l'événement</h3>
              <button 
                onClick={() => setShowIdModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded">
              <code className="text-sm font-mono text-white">{event.id}</code>
              <button 
                onClick={() => {
                  navigator.clipboard.writeText(event.id);
                  showToast('ID copié!', 'success');
                }}
                className="ml-4 text-gray-400 hover:text-white"
                title="Copier"
              >
                <Copy size={16} />
              </button>
            </div>
            <button 
              onClick={() => setShowIdModal(false)}
              className="mt-4 w-full py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-md"
            >
              Fermer
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <Link
            to="/events/admin"
            className="text-yellow-400 hover:text-yellow-300 flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Retour aux événements
          </Link>
        </div>
        
        <div className="sm:flex sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Participants à {event.title}</h1>
            <p className="mt-2 text-sm text-gray-300">
              {formatDate(event.start_date.toString())}
              {event.location && ` • ${event.location}`}
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-3">
            <Link
              to={`/events/admin/${event.id}/tickets`}
              className="inline-flex items-center px-4 py-2 border border-white/10 rounded-md shadow-sm text-sm font-medium text-gray-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400/50"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
              </svg>
              Gérer les tickets
            </Link>
            
            <Link
              to={`/events/control/${event.slug}`}
              target="_blank"
              className="inline-flex items-center px-4 py-2 border border-white/10 rounded-md shadow-sm text-sm font-medium text-gray-200 bg-white/5 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-400/50"
            >
              <svg className="-ml-1 mr-2 h-5 w-5 text-yellow-400/70" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
              </svg>
              Page client
            </Link>
            
            <button 
              onClick={() => setShowIdModal(true)}
              className="inline-flex items-center px-3 py-2 border border-white/10 rounded-md text-sm text-gray-300 hover:bg-white/5"
              title="Voir l'ID de l'événement"
            >
              <Eye className="h-5 w-5 text-yellow-400/70 mr-1" />
              <span>ID</span>
            </button>
          </div>
        </div>
        
        <div className="bg-black/90 shadow-sm rounded-lg p-6 mb-8 border border-white/10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 mb-6">
            <div className="w-full md:w-1/2">
              <label htmlFor="search" className="sr-only">Rechercher</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  id="search"
                  type="search"
                  className="block w-full pl-10 pr-3 py-2 border border-white/10 rounded-md leading-5 bg-black/50 text-gray-200 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-yellow-400/50 focus:border-yellow-400/50 sm:text-sm"
                  placeholder="Rechercher par nom, email ou QR code"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                onClick={() => setFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                  filter === 'all'
                    ? 'bg-yellow-400 text-black'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
              >
                Tous
              </button>
              <button
                type="button"
                onClick={() => setFilter('checked_in')}
                className={`px-4 py-2 text-sm font-medium ${
                  filter === 'checked_in'
                    ? 'bg-yellow-400 text-black'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
              >
                Enregistrés
              </button>
              <button
                type="button"
                onClick={() => setFilter('pending')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                  filter === 'pending'
                    ? 'bg-yellow-400 text-black'
                    : 'bg-white/10 text-gray-200 hover:bg-white/20'
                }`}
              >
                En attente
              </button>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full align-middle">
              {filteredAttendees.length === 0 ? (
                <div className="text-center py-10">
                  <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-yellow-400">Aucun participant trouvé</h3>
                  <p className="mt-1 text-sm text-gray-400">
                    {searchTerm ? 'Essayez avec une autre recherche.' : 'Aucun participant pour cet événement.'}
                  </p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-700">
                  <thead className="bg-black/60">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Participant
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Ticket
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        QR Code
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-black/30 divide-y divide-gray-700">
                    {paginatedAttendees.map((attendee, index) => (
                      <tr key={attendee.id} className={index % 2 === 0 ? 'bg-white/5' : 'bg-white/10'}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {attendee.user?.avatar_url ? (
                                <img
                                  className="h-10 w-10 rounded-full"
                                  src={attendee.user.avatar_url}
                                  alt={attendee.user.name || ''}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-700 flex items-center justify-center">
                                  <span className="text-gray-300 font-medium">
                                    {attendee.user?.name?.charAt(0) || attendee.attendee_name?.charAt(0) || 'U'}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-200">
                                {attendee.user?.name || attendee.attendee_name || 'Anonyme'}
                              </div>
                              <div className="text-sm text-gray-400">
                                {attendee.user?.email || attendee.attendee_email || 'Pas d\'email'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-blue-400 font-medium">{attendee.ticket_order?.ticket?.name || 'N/A'}</td>
                        <td className="px-4 py-2 text-purple-400">{attendee.qr_code}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            attendee.status === 'checked_in' 
                              ? 'bg-green-900/30 text-green-400' 
                              : 'bg-yellow-900/30 text-yellow-400'
                          }`}>
                            {attendee.status === 'checked_in' ? 'Enregistré' : 'En attente'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {attendee.check_in_time 
                            ? formatDate(attendee.check_in_time.toString()) 
                            : formatDate(attendee.created_at.toString())}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {attendee.status === 'pending' ? (
                            <button
                              onClick={() => handleCheckIn(attendee.id)}
                              disabled={checkingInId === attendee.id}
                              className="text-yellow-400 hover:text-yellow-300"
                            >
                              {checkingInId === attendee.id ? (
                                <span className="flex items-center">
                                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-yellow-400 mr-2"></div>
                                  Enregistrement...
                                </span>
                              ) : (
                                'Enregistrer'
                              )}
                            </button>
                          ) : (
                            <span className="text-gray-500">Déjà enregistré</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          
          {/* Pagination */}
          <div className="mt-6 border-t border-white/10 pt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                <p className="text-sm text-gray-400">
                  Affichage de <span className="font-medium text-white">
                    {Math.min((currentPage - 1) * itemsPerPage + 1, totalFiltered)}
                  </span> à <span className="font-medium text-white">
                    {Math.min(currentPage * itemsPerPage, totalFiltered)}
                  </span> sur <span className="font-medium text-white">
                    {totalFiltered}
                  </span> participants
                </p>
                <select
                  value={itemsPerPage}
                  onChange={(e) => setItemsPerPage(Number(e.target.value))}
                  className="ml-2 bg-transparent border border-white/10 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-yellow-400/50"
                >
                  {itemsPerPageOptions.map((option) => (
                    <option key={option} value={option} className="bg-black text-white">
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md ${currentPage === 1 ? 'text-gray-500 cursor-not-allowed' : 'text-white hover:bg-white/5'}`}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="text-sm text-gray-300">
                  Page {currentPage} sur {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md ${currentPage === totalPages ? 'text-gray-500 cursor-not-allowed' : 'text-white hover:bg-white/5'}`}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EventAttendees;
